import { useContext, type KeyboardEvent } from 'react';
import { useGT } from 'gt-react';
import type { Table } from '@/lib/tanstack-table';
import { Input } from '@/components/ui/input';
import {
  useCreateOverflowLine,
  useOverrideOption,
  useSetSuggestionStatuses,
  type AssignmentLine,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { toast } from 'src/components/ui/toast';
import { getCartLineTableMeta } from './cartLineTableMeta';
import { needQuantity, planNeedQuantitySaves } from './needQuantitySaves';
import {
  handleQuantityKeyDown,
  quantityDraft,
  selectOnFirstClick,
} from './quantityColumnKeyboard';
import { QuantityDraftContext } from './quantityDraftContext';
import { useLineQuantityDraft } from './useLineQuantityDraft';
import { settledMutationCounts } from '../utils/settledMutationCounts';
import type { NeedGroup } from '../utils/needRows';

/**
 * The need row's quantity (GEN-2938): the total of its open SO lines. A typed
 * total is spread over them, oldest requested date first, with the rest on its
 * stock line, or on a new one when it has none (planNeedQuantitySaves). Keys work as in the line quantity column.
 */
export function NeedQuantityCell({
  need,
  table,
}: {
  need: NeedGroup;
  table: Table<AssignmentLine>;
}) {
  const gt = useGT();
  const meta = getCartLineTableMeta(table);
  const override = useOverrideOption();
  const createOverflowLine = useCreateOverflowLine();
  const setStatuses = useSetSuggestionStatuses();
  const { draft, shownDraft, isSaving, setDraft, track } = useLineQuantityDraft(
    need.id,
    override.isPending || createOverflowLine.isPending || setStatuses.isPending,
  );
  // The need's total is read from its SO lines, so it waits for their saves,
  // and its own save locks every one of them until it settles.
  const memberIds = need.open.map((line) => line.suggestionId);
  const shared = useContext(QuantityDraftContext);
  const memberSaving = memberIds.some((id) => shared?.pending.has(id));
  const total = needQuantity(need.open);
  const reset = () => setDraft(null);

  const save = () => {
    if (draft == null) return;
    const plan = planNeedQuantitySaves({
      open: need.open,
      value: draft,
      supplierId: meta.supplierId,
      sourceWarehouseId: meta.sourceWarehouseId,
    });
    if (plan.kind !== 'save') {
      reset();
      if (plan.kind === 'too-low') {
        toast.error(
          gt('Each line keeps at least 1. Enter {minimum} or more.', {
            minimum: plan.minimum,
            context:
              'buyer board need row quantity below one per line (sales-order and stock lines)',
          }),
        );
      } else if (plan.kind === 'too-high') {
        toast.error(
          gt(
            'A special order buys only what its orders need. Enter {maximum} or less.',
            {
              maximum: plan.maximum,
              context:
                'buyer board need row quantity above a special order (build-to-order) item’s sales-order lines; no stock line is added',
            },
          ),
        );
      } else if (plan.kind === 'missing-context') {
        toast.error(
          gt('Could not update quantity for this plan.', {
            context: 'replenishment inline quantity update error',
          }),
        );
      }
      return;
    }
    const spreadTotal = draft;
    const { overflowLine, trash } = plan;
    const writes = [
      ...plan.saves.map((input) => () => override.mutateAsync(input)),
      ...(overflowLine == null
        ? []
        : [() => createOverflowLine.mutateAsync(overflowLine)]),
      ...(trash.length === 0
        ? []
        : [
            () =>
              setStatuses.mutateAsync({
                suggestionIds: trash,
                status: 'rejected',
              }),
          ]),
    ];
    void track(
      settledMutationCounts(writes, (write) => write()),
      memberIds,
    ).then(({ failed }) => {
      reset();
      if (failed > 0) {
        toast.error(
          gt('Could not update quantity. Try again.', {
            context: 'replenishment inline quantity update error',
          }),
        );
        return;
      }
      toast.success(
        trash.length > 0
          ? gt('Spread {total} over the SO lines and trashed the stock line', {
              total: spreadTotal,
              context:
                'buyer board need row quantity lowered below its sales-order lines, so its stock line was trashed',
            })
          : overflowLine == null
            ? gt('Spread {total} over {count} lines, SOs oldest first', {
                total: spreadTotal,
                count: plan.lineCount,
                context: 'buyer board need row quantity spread confirmation',
              })
            : gt('Spread {total}: {extra} on a new stock line', {
                total: spreadTotal,
                extra:
                  overflowLine.buyQuantity ?? overflowLine.transferQuantity,
                context:
                  'buyer board need row quantity spread confirmation, overflow on a new stock line',
              }),
      );
    });
  };

  // Saving the total locks the SO lines; landing on one would lose focus.
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) =>
    handleQuantityKeyDown(event, {
      revert: reset,
      locking: () => new Set(draft == null ? [] : memberIds),
    });

  return (
    <Input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={shownDraft ?? String(total)}
      disabled={isSaving || memberSaving}
      aria-label={gt('Total order quantity for {count} lines', {
        count: need.open.length,
        context: 'buyer board need row quantity input label',
      })}
      // LINE_QUANTITY_ATTRIBUTE: Enter and the arrows step through it too.
      data-line-quantity={need.id}
      data-need-members={memberIds.join(' ')}
      onChange={(event) => {
        const next = quantityDraft(event.target.value);
        if (next !== null) setDraft(next);
      }}
      onFocus={(event) => event.currentTarget.select()}
      onMouseDown={selectOnFirstClick}
      onBlur={save}
      onKeyDown={handleKeyDown}
      onClick={(event) => event.stopPropagation()}
      className="h-7 w-20 scroll-mt-8 px-2 text-right text-xs font-medium tabular-nums group-data-[density=compact]/table:h-6"
    />
  );
}
